import { createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import { getParticipantRounds } from './thunks'
import { UserChallengeRound } from 'api/types'

interface ParticipantRoundsEntry {
  rounds: UserChallengeRound[]
  status: RequestStatus
  error: string | null
}

interface ParticipantRoundsState {
  // keyed by participantId
  challengeLeaderboard: {
    [participantId: string]: ParticipantRoundsEntry
  }
}

const initialState: ParticipantRoundsState = {
  challengeLeaderboard: {},
}

const getEntry = (state: ParticipantRoundsState, participantId: string): ParticipantRoundsEntry => {
  if (!state.challengeLeaderboard[participantId]) {
    state.challengeLeaderboard[participantId] = { rounds: [], status: 'idle', error: null }
  }
  return state.challengeLeaderboard[participantId]
}

const participantRoundsSlice = createSlice({
  name: 'participantRounds',
  initialState,
  reducers: {
    resetParticipantRounds: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getParticipantRounds.pending, (state, action) => {
        const entry = getEntry(state, action.meta.arg.participantId)
        entry.status = 'loading'
        entry.error = null
      })
      .addCase(getParticipantRounds.fulfilled, (state, action) => {
        const entry = getEntry(state, action.meta.arg.participantId)
        entry.status = 'succeeded'
        entry.rounds = action.payload.data ?? []
      })
      .addCase(getParticipantRounds.rejected, (state, action) => {
        const entry = getEntry(state, action.meta.arg.participantId)
        entry.status = 'failed'
        entry.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
  },
})

export const { resetParticipantRounds } = participantRoundsSlice.actions

export const selectParticipantRounds = (state: RootState) => state.participantRounds.challengeLeaderboard

export const selectParticipantRoundsForChallengeLeaderboard =
  (participantId: string | null | undefined) => (state: RootState) =>
    participantId ? state.participantRounds.challengeLeaderboard[participantId] : undefined

export default participantRoundsSlice.reducer
